// @core/stores/form/FormFormPolicyStore.ts
// Auto-generated by corebuilder. DO NOT EDIT.

import { makeAutoObservable, runInAction } from 'mobx';
import { formApi } from '@core/api';
import type {
  CreateFormPolicyRequest,
  FormPolicy,
  ResolveFormContextRequest,
  ResolveFormContextResponse,
  UpdateFormPolicyRequest,
} from '@core/types/form';
import type { PaginatedRequest, PageInfo } from '@core/types/pagination';

class FormFormPolicyStore {
  // List state
  items: FormPolicy[] = [];
  pageInfo: PageInfo | null = null;

  // Single item state
  currentItem: FormPolicy | null = null;

  // Loading states
  isLoading = false;
  isLoadingItem = false;
  isSaving = false;
  isDeleting = false;

  // Error state (stores full ApiClientError for request context)
  error: unknown = null;
  // Cache by ID
  private cache = new Map<string, FormPolicy>();

  constructor() {
    makeAutoObservable(this);
  }
  // Computed
  get hasMore(): boolean {
    return this.pageInfo?.has_next ?? false;
  }

  get isEmpty(): boolean {
    return this.items.length === 0 && !this.isLoading;
  }

  // Cache helpers
  getById(id: string): FormPolicy | undefined {
    return this.cache.get(id);
  }
  private updateCache(item: FormPolicy | undefined): void {
    if (item?.entity?.id) {
      this.cache.set(item.entity.id, item);
    }
  }
  private removeFromCache(id: string): void {
    this.cache.delete(id);
  }

  // Actions
  setError(error: unknown): void {
    this.error = error;
  }

  clearError(): void {
    this.error = null;
  }
  clearCurrent(): void {
    this.currentItem = null;
  }

  reset(): void {
    this.items = [];
    this.pageInfo = null;
    this.currentItem = null;
    this.error = null;
    this.cache.clear();
  }

  /**
   * Create form policy
   */
  async CreateFormPolicy(data: CreateFormPolicyRequest): Promise<FormPolicy> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await formApi.CreateFormPolicy(data);
      const item = response.policy;

      runInAction(() => {
        if (item) {
          this.items = [item, ...this.items];
          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }

  /**
   * Delete form policy
   */
  async DeleteFormPolicy(policy_id: string): Promise<void> {
    this.isDeleting = true;
    this.error = null;

    try {
      await formApi.DeleteFormPolicy(policy_id);

      runInAction(() => {
        // Remove from list
        this.items = this.items.filter(i => i.entity?.id !== policy_id);

        // Clear current item if same
        if (this.currentItem?.entity?.id === policy_id) {
          this.currentItem = null;
        }

        this.removeFromCache(policy_id);
        this.isDeleting = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isDeleting = false;
      });
      throw error;
    }
  }

  /**
   * Get form policy
   */
  async GetFormPolicy(policy_id: string): Promise<FormPolicy> {
    // Check cache first
    const cached = this.cache.get(policy_id);
    if (cached) {
      this.currentItem = cached;
      return cached;
    }

    this.isLoadingItem = true;
    this.error = null;

    try {
      const response = await formApi.GetFormPolicy(policy_id);
      const item = response.policy;

      runInAction(() => {
        this.currentItem = item ?? null;
        this.updateCache(item);
        this.isLoadingItem = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoadingItem = false;
      });
      throw error;
    }
  }

  /**
   * Get entity form policy
   */
  async GetFormPolicyByEntity(entity_type: string, entity_id: string): Promise<FormPolicy> {
    // Check cache first
    const cached = this.cache.get(entity_id);
    if (cached) {
      this.currentItem = cached;
      return cached;
    }

    this.isLoadingItem = true;
    this.error = null;

    try {
      const response = await formApi.GetFormPolicyByEntity(entity_type, entity_id);
      const item = response.policy;

      runInAction(() => {
        this.currentItem = item ?? null;
        this.updateCache(item);
        this.isLoadingItem = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoadingItem = false;
      });
      throw error;
    }
  }

  /**
   * List form policies
   */
  async ListFormPolicies(params?: PaginatedRequest): Promise<void> {
    this.isLoading = true;
    this.error = null;

    try {
      const response = await formApi.ListFormPolicies(params);

      runInAction(() => {
        this.items = response.policies?.policies ?? [];
        this.pageInfo = response.pagination?.page_info ?? null;
        this.isLoading = false;

        // Update cache
        this.items.forEach(item => this.updateCache(item));
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Resolve form context
   */
  async ResolveFormContext(data: ResolveFormContextRequest): Promise<ResolveFormContextResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await formApi.ResolveFormContext(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update form policy
   */
  async UpdateFormPolicy(policy_id: string, data: UpdateFormPolicyRequest): Promise<FormPolicy> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await formApi.UpdateFormPolicy(policy_id, data);
      const item = response.policy;

      runInAction(() => {
        if (item) {
          // Update in list
          const index = this.items.findIndex(i => i.entity?.id === item.entity?.id);
          if (index !== -1) {
            this.items[index] = item;
          }

          // Update current item if same
          if (this.currentItem?.entity?.id === item.entity?.id) {
            this.currentItem = item;
          }

          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }
}

export const formFormPolicyStore = new FormFormPolicyStore();
